import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const MacBookWasserschadenPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook Wasserschaden – Sofortmaßnahmen & Reparatur ab 119€ | PhoneMedic',
      description: 'MacBook Wasserschaden? ✓ Sofort abschalten ✓ Platinen-Reinigung & Reparatur ab 119€ ✓ 70–90% Erfolgsquote ✓ 12 Monate Garantie ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['MacBook Wasserschaden', 'MacBook ins Wasser gefallen', 'MacBook Flüssigkeitsschaden', 'MacBook Reparatur Wasserschaden', 'MacBook Platine reinigen'],
      canonicalUrl: 'https://www.phone-medic.de/macbook-wasserschaden',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'MacBook Wasserschaden Reparatur',
        description: 'Professionelle Platinen-Reinigung und Chip-Level Reparatur nach MacBook Wasserschaden. Korrosion entfernen, defekte Bauteile tauschen, Erfolgsquote 70–90%.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: { '@type': 'Offer', price: '119.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was soll ich sofort tun wenn mein MacBook nass geworden ist?',
            acceptedAnswer: { '@type': 'Answer', text: '1. Sofort ausschalten – falls noch an. 2. NICHT mehr einschalten oder laden. 3. Nicht mit Fön trocknen. 4. Auf der Seite oder umgedreht ablegen damit Flüssigkeit ablaufen kann. 5. So schnell wie möglich zur professionellen Reinigung einsenden.' },
          },
          {
            '@type': 'Question',
            name: 'Wie hoch ist die Erfolgsquote bei MacBook Wasserschäden?',
            acceptedAnswer: { '@type': 'Answer', text: 'Bei uns liegt die Erfolgsquote bei 70–90%, abhängig von der Schwere des Schadens und wie schnell das Gerät nach dem Schaden ausgeschaltet wurde. Je schneller das MacBook nach dem Wasserschaden ausgeschaltet wird, desto besser die Prognose.' },
          },
          {
            '@type': 'Question',
            name: 'Repariert Apple MacBooks mit Wasserschaden?',
            acceptedAnswer: { '@type': 'Answer', text: 'Apple lehnt Wasserschäden grundsätzlich ab. Die Kosten für ein neues Logicboard liegen bei 800–1.500 €. Wir reparieren dagegen die tatsächlich beschädigten Bauteile auf der Platine – ab 119 €.' },
          },
          {
            '@type': 'Question',
            name: 'Mein MacBook startet noch – muss ich trotzdem zur Reparatur?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja, dringend. Flüssigkeit verursacht zunächst oft keine sichtbaren Symptome. Korrosion breitet sich langsam aus und kann Wochen später zu einem kompletten Ausfall führen. Je früher die Platine gereinigt wird, desto besser.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet die Wasserschaden Reparatur?',
            acceptedAnswer: { '@type': 'Answer', text: 'Diagnose und Platinen-Reinigung ab 119 €. Je nach Schwere des Schadens und Anzahl der defekten Bauteile können weitere Kosten entstehen. Du erhältst immer zuerst einen verbindlichen Kostenvoranschlag.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#macbook-wasserschaden-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-wasserschaden-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#macbook-wasserschaden-schema')?.remove() }
  }, [])

  const sofortmassnahmen = [
    { step: '1', title: 'Sofort ausschalten', desc: 'Halte den Einschaltknopf 5 Sekunden gedrückt. NICHT auf normales Herunterfahren warten.' },
    { step: '2', title: 'Nicht mehr einschalten', desc: 'Das ist die wichtigste Regel. Strom durch eine feuchte Platine verursacht Kurzschlüsse und Korrosion.' },
    { step: '3', title: 'Nicht mit Fön trocknen', desc: 'Wärme treibt die Korrosion an und kann Chips dauerhaft beschädigen. Raumtemperatur ist besser.' },
    { step: '4', title: 'Ablaufen lassen', desc: 'MacBook auf die Seite oder umgedreht legen damit überschüssige Flüssigkeit ablaufen kann.' },
    { step: '5', title: 'Schnell einsenden', desc: 'Jede Stunde zählt. Korrosion beginnt sofort und breitet sich aus. Je früher die Reinigung, desto besser.' },
  ]

  const faqs = [
    { q: 'Was soll ich sofort tun wenn mein MacBook nass geworden ist?', a: '1. Sofort ausschalten. 2. Nicht nochmal einschalten. 3. Keinen Fön benutzen. 4. Auf der Seite ablegen. 5. So schnell wie möglich einsenden.' },
    { q: 'Wie hoch ist die Erfolgsquote bei Wasserschäden?', a: 'Bei uns 70–90%, abhängig vom Schweregrad und wie schnell das Gerät ausgeschaltet wurde. Je früher das MacBook ausgeschaltet wird, desto besser.' },
    { q: 'Repariert Apple MacBooks mit Wasserschaden?', a: 'Nein. Apple lehnt Wasserschäden grundsätzlich ab oder verlangt 800–1.500 € für ein neues Logicboard. Wir reparieren die beschädigten Bauteile ab 119 €.' },
    { q: 'Mein MacBook startet noch – muss ich trotzdem zur Reparatur?', a: 'Ja, dringend. Korrosion breitet sich langsam aus und kann Wochen später zu einem kompletten Ausfall führen. Je früher die Platine gereinigt wird, desto besser.' },
    { q: 'Was kostet die Wasserschaden Reparatur?', a: 'Diagnose und Reinigung ab 119 €. Du erhältst immer zuerst einen Kostenvoranschlag bevor die Reparatur beginnt.' },
    { q: 'Bleiben meine Daten erhalten?', a: 'In den meisten Fällen ja. Der Speicherchip wird bei der Platinen-Reinigung nicht beschädigt. Wir informieren dich vorher falls der Speicher betroffen sein könnte.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 text-blue-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
              Wasserschaden · Platinen-Reinigung · Chip-Level
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook Wasserschaden –<br />
              <span className="text-indigo-400">Sofortmaßnahmen & Reparatur</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Kaffee, Wasser oder andere Flüssigkeiten? Nicht einschalten, sofort einsenden. Wir reinigen die Platine und reparieren beschädigte Chips – Erfolgsquote 70–90 %.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Jetzt einsenden
              </a>
              <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                WhatsApp – Notfall
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: '70–90 %', label: 'Erfolgsquote' },
              { number: 'ab 119 €', label: 'Reparatur' },
              { number: '24,95 €', label: 'Diagnose' },
              { number: '12 Monate', label: 'Garantie' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-red-50 border-t border-red-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-red-900 mb-3">Sofortmaßnahmen – jetzt sofort handeln</h2>
            <p className="text-red-700">Jede Minute zählt. Korrosion beginnt sobald Flüssigkeit die Platine berührt.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {sofortmassnahmen.map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-red-200 p-5 text-center">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-3 text-sm">{m.step}</div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{m.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Was passiert bei einem Wasserschaden?</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Wasser an sich richtet zunächst wenig Schaden an – das Problem ist das Salz und die Mineralien im Wasser. Sie hinterlassen Rückstände auf der Platine, die mit der Zeit zu Korrosion führen. Diese Korrosion unterbricht Leiterbahnen und beschädigt Chips.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Besonders tückisch: Das MacBook kann nach einem Wasserschaden noch einige Tage funktionieren – bevor die Korrosion einen Kurzschluss verursacht und es komplett aufhört zu starten.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Unter dem Mikroskop reinigen wir die Platine mit Ultraschall und Isopropanol, entfernen Korrosion bis auf die Leiterbahn-Ebene und tauschen beschädigte SMD-Bauteile aus.
              </p>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                <h3 className="font-bold text-indigo-900 mb-3">Unser Vorgehen bei Wasserschäden</h3>
                <ul className="space-y-2 text-sm text-indigo-800">
                  {[
                    'Platine ausbauen und unter dem Mikroskop untersuchen',
                    'Ultraschallreinigung mit Isopropanol',
                    'Korrosion auf Leiterbahn-Ebene entfernen',
                    'Defekte Chips und SMD-Bauteile ersetzen',
                    'Funktionstest nach Reparatur',
                  ].map((step, i) => (
                    <li key={i} className="flex gap-2"><span className="text-indigo-600 shrink-0">✓</span>{step}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-3 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
                  <span>Leistung</span>
                  <span className="text-center">Apple</span>
                  <span className="text-center">PhoneMedic</span>
                </div>
                {[
                  { label: 'Platinen-Reinigung', apple: false, pm: true },
                  { label: 'Chip-Level Reparatur', apple: false, pm: true },
                  { label: 'Daten erhalten', apple: false, pm: true },
                  { label: 'Kostenvoranschlag', apple: false, pm: true },
                  { label: 'Reparatur ab 119 €', apple: false, pm: true },
                  { label: 'Logicboard-Tausch 800–1.500 €', apple: true, pm: false },
                ].map((item, i) => (
                  <div key={i} className={`grid grid-cols-3 px-6 py-3 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-gray-700">{item.label}</span>
                    <span className={`text-center font-bold ${item.apple ? 'text-green-600' : 'text-red-400'}`}>{item.apple ? '✓' : '✗'}</span>
                    <span className={`text-center font-bold ${item.pm ? 'text-green-600' : 'text-red-400'}`}>{item.pm ? '✓' : '✗'}</span>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Wichtig:</strong> Auch wenn das MacBook nach dem Wasserschaden noch startet, sollte es so schnell wie möglich zur Reinigung. Korrosion breitet sich unsichtbar aus und kann jederzeit zum Totalausfall führen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere MacBook Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'MacBook startet nicht', path: '/macbook-startet-nicht' },
              { label: 'MacBook Akku tauschen', path: '/macbook-akku-tauschen' },
              { label: 'MacBook Display Reparatur', path: '/macbook-display-reparatur' },
              { label: 'Logicboard Reparatur', path: '/macbook-logicboard-reparatur' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">MacBook nass geworden? Sofort handeln.</h2>
          <p className="text-gray-400 mb-8">Reparatur ab 119 € · Kostenvoranschlag vor Reparaturbeginn · 12 Monate Garantie</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <a href="https://wa.me/491605010907" target="_blank" rel="noopener noreferrer" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              WhatsApp – Notfall
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookWasserschadenPage
